import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDispatchApp } from "../lib/redux";
import {
  startGetScheduledContents,
  startListenerScheduledContents,
} from "../actions/user";
import { getPathDatabaseByRole } from "../actions/auth";
import { META, STATES } from "../utils/constants";
import { GRADIENT_BLACK_COLOR } from "../utils/colors";
import { off, ref } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import { useTranslationApp } from "../lib/i18next";
import { startOfMonth, startOfDay } from "date-fns";
import {
  STATUS_CONFIG,
  resolveCalendarEventStatus,
} from "../utils/calendarEventPublishStatus";
import {
  buildCalendarEvents,
  getCalendarPlatformEventColor,
} from "../utils/calendarEvents";
import {
  getBrowserTimezone,
  getZonedNow,
  getZonedStartOfDay,
} from "../utils/timezone";
import { selectCalendarActiveAutomations } from "../utils/calendarActiveAutomations";
import { getIntlLocaleCode } from "../utils/i18nLanguage";

import { LaunchError } from "../utils/errors";

const getPeriodStartDate = (date, view) => {
  const byView = {
    month: startOfMonth,
    week: startOfDay,
    day: startOfDay,
  };
  return (byView[view] ?? ((d) => d))(date);
};

const isNavigatingToPastPeriod = (targetDate, view, today) => {
  return getPeriodStartDate(targetDate, view) < getPeriodStartDate(today, view);
};

const canGoToPreviousPeriod = (currentDate, view, today) => {
  return (
    getPeriodStartDate(currentDate, view) > getPeriodStartDate(today, view)
  );
};

const buildEventStyle = (overrides) => ({
  ...overrides,
  color: "white",
  borderRadius: "8px",
  border: "none",
  padding: "4px 6px",
  fontSize: "12px",
});

export const useModalCalendar = (
  modalOpen,
  { navigateToScheduledRequest = null, onNavigateToScheduledConsumed } = {}
) => {
  const dispatch = useDispatchApp();
  const { i18n, t } = useTranslationApp();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [view, setView] = useState("week");
  const [date, setDate] = useState(new Date());

  const anchorRef = useRef(null);
  const listenerRef = useRef(null);
  const namesUnsubscribeRef = useRef(null);
  const prevModalOpenRef = useRef(false);

  const [scheduledContents, setScheduledContents] = useState({});
  const [publishingAutomations, setPublishingAutomations] = useState({});

  const automationNames = useMemo(
    () =>
      Object.entries(publishingAutomations).reduce(
        (result, [automationID, automation]) => {
          if (automation?.name) {
            result[automationID] = automation.name;
          }
          return result;
        },
        {}
      ),
    [publishingAutomations]
  );

  const activeAutomations = useMemo(
    () =>
      selectCalendarActiveAutomations({ automations: publishingAutomations }),
    [publishingAutomations]
  );

  const calendarTimezone = getBrowserTimezone();

  const culture = useMemo(
    () => getIntlLocaleCode(i18n.language),
    [i18n.language]
  );

  const isPopoverOpen = useMemo(
    () => Boolean(anchorEl && selectedEvent),
    [anchorEl, selectedEvent]
  );

  const refreshScheduledContents = useCallback(
    async (showLoading = false) => {
      if (!modalOpen) return;

      if (showLoading) setIsLoading(true);

      try {
        const fetchedScheduledContents = await dispatch(
          startGetScheduledContents()
        );
        if (fetchedScheduledContents !== false) {
          setScheduledContents(fetchedScheduledContents || {});
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "useModalCalendar",
            capture: "exception",
          })
        );
        console.error("Error refreshing scheduled contents:", error);
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [modalOpen, dispatch]
  );

  useEffect(() => {
    refreshScheduledContents(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    setCalendarEvents(
      buildCalendarEvents({ scheduledContents, t, automationNames })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledContents, automationNames, i18n.language]);

  useEffect(() => {
    if (!selectedEvent) return;
    const nextEvent = calendarEvents.find(
      (event) => event.id === selectedEvent.id
    );
    if (
      nextEvent &&
      nextEvent.automationName !== selectedEvent.automationName
    ) {
      setSelectedEvent(nextEvent);
    }
  }, [calendarEvents, selectedEvent]);

  useEffect(() => {
    if (!modalOpen) {
      if (listenerRef.current) {
        off(listenerRef.current);
        listenerRef.current = null;
      }
      if (namesUnsubscribeRef.current) {
        namesUnsubscribeRef.current();
        namesUnsubscribeRef.current = null;
      }
      return;
    }

    dispatch(
      startListenerScheduledContents(
        (updatedContents) => {
          setScheduledContents(updatedContents || {});
        },
        (refValue) => {
          listenerRef.current = refValue;
        }
      )
    );

    const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
    if (advertiserID) {
      const automationsRef = ref(db, `publishingAutomations/${advertiserID}`);
      namesUnsubscribeRef.current = onValue(automationsRef, (snapshot) => {
        const value = snapshot.exists() ? snapshot.val() : {};
        setPublishingAutomations(value || {});
      });
    }

    return () => {
      if (listenerRef.current) {
        off(listenerRef.current);
        listenerRef.current = null;
      }
      if (namesUnsubscribeRef.current) {
        namesUnsubscribeRef.current();
        namesUnsubscribeRef.current = null;
      }
    };
  }, [modalOpen, dispatch]);

  useEffect(() => {
    if (!modalOpen) {
      setAnchorEl(null);
      setSelectedEvent(null);
      setView("week");
      setDate(getZonedNow({ timezone: calendarTimezone }));
      prevModalOpenRef.current = false;
      return;
    }

    const justOpened = !prevModalOpenRef.current;
    prevModalOpenRef.current = true;

    if (justOpened && !navigateToScheduledRequest?.date) {
      setDate(getZonedNow({ timezone: calendarTimezone }));
    }
  }, [modalOpen, navigateToScheduledRequest, calendarTimezone]);

  useEffect(() => {
    if (!modalOpen || !navigateToScheduledRequest?.date) return;
    setDate(navigateToScheduledRequest.date);
    setView("week");
    onNavigateToScheduledConsumed?.();
  }, [modalOpen, navigateToScheduledRequest, onNavigateToScheduledConsumed]);

  const onClosePopover = useCallback(() => {
    setAnchorEl(null);
    setSelectedEvent(null);
  }, []);

  const onEventClick = useCallback((event, e) => {
    const eventElement =
      e?.currentTarget ||
      document.querySelector(`[data-event-id="${event.id}"]`);

    if (!eventElement || !anchorRef.current) return;

    const rect = eventElement.getBoundingClientRect();
    anchorRef.current.style.cssText = `
      position: fixed;
      top: ${rect.bottom}px;
      left: ${rect.left}px;
      width: ${rect.width || 200}px;
      height: 1px;
      visibility: hidden;
    `;

    setAnchorEl(anchorRef.current);
    setSelectedEvent(event);
  }, []);

  const onShowMore = useCallback((selectedDate) => {
    setView("day");
    setDate(selectedDate);
  }, []);

  const onViewChange = useCallback(
    (newView) => {
      setView(newView);

      const today = getZonedNow({ timezone: calendarTimezone });
      if (isNavigatingToPastPeriod(date, newView, today)) {
        setDate(getPeriodStartDate(today, newView));
      }
    },
    [date, calendarTimezone]
  );

  const onNavigate = useCallback(
    (newDateOrAction) => {
      const today = getZonedNow({ timezone: calendarTimezone });

      if (typeof newDateOrAction === "string") {
        if (
          newDateOrAction === "PREV" &&
          !canGoToPreviousPeriod(date, view, today)
        ) {
          return;
        }
        return;
      }

      const targetDate = newDateOrAction;

      if (view === "month") {
        const todayStart = getZonedStartOfDay({ timezone: calendarTimezone });
        const targetStart = getZonedStartOfDay({
          date: targetDate,
          timezone: calendarTimezone,
        });

        if (targetStart < todayStart) return;
      }

      if (isNavigatingToPastPeriod(targetDate, view, today)) {
        setDate(getPeriodStartDate(today, view));
        return;
      }

      setDate(targetDate);
    },
    [view, date, calendarTimezone]
  );

  const eventPropGetter = useCallback((event) => {
    const isMultiPlatformPost = event.platforms.length > 1;

    const state = resolveCalendarEventStatus(event.states);
    const { opacity } = STATUS_CONFIG[state] ?? STATUS_CONFIG[STATES.SCHEDULED];

    let backgroundStyleFragment;
    if (isMultiPlatformPost) {
      backgroundStyleFragment = {
        background: GRADIENT_BLACK_COLOR,
        opacity,
      };
    } else {
      const platformKey = event.platforms[0] || event.platform || META;
      backgroundStyleFragment = {
        backgroundColor: getCalendarPlatformEventColor(platformKey),
        opacity,
      };
    }

    let classNames = "";

    if (event.publishIssue === "failure") {
      classNames = "calendar-event-publish--failure";
    } else if (event.publishIssue === "partial") {
      classNames = "calendar-event-publish--partial-failure";
    }

    return {
      className: classNames,
      style: buildEventStyle(backgroundStyleFragment),
    };
  }, []);

  useEffect(() => {
    if (!isPopoverOpen) return;

    const onClickOutside = (e) => {
      const popover = document.querySelector('[role="tooltip"]');
      const eventElement = document.querySelector(
        `[data-event-id="${selectedEvent?.id}"]`
      );

      if (
        popover &&
        !popover.contains(e.target) &&
        eventElement &&
        !eventElement.contains(e.target)
      ) {
        onClosePopover();
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isPopoverOpen, selectedEvent, onClosePopover]);

  return {
    anchorEl,
    selectedEvent,
    events: calendarEvents,
    isLoading,
    view,
    date,
    culture,
    open: isPopoverOpen,
    anchorRef,
    onEventClick,
    onClosePopover,
    onShowMore,
    onViewChange,
    onNavigate,
    eventPropGetter,
    refreshScheduledContents,
    calendarTimezone,
    activeAutomations,
  };
};
